import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCheck, ListTodo, Plus } from 'lucide-react';
import type { Customer, Resource, Task, TaskPriority } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent } from '../api/realtime';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
  ReachDot,
} from '../components/ui';

const PRIORITY_INTENT: Record<TaskPriority, 'bad' | 'warn' | 'info' | 'neutral'> = {
  urgent: 'bad',
  high: 'warn',
  normal: 'info',
  low: 'neutral',
};

export interface TaskAssignee {
  id: string;
  name: string;
  email: string;
}

export function TasksPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('open');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, reload } = useFetch(
    () => api.get<Task[]>('/api/tasks', { state: state || undefined, limit: 100 }),
    [state],
    { refreshMs: 45_000 },
  );
  const { data: assignees } = useFetch(() => api.get<TaskAssignee[]>('/api/tasks/assignees'), []);

  useRealtimeEvent(['tasks'], (event) => {
    if (event.type === 'task.created' || event.type === 'task.updated' || event.type === 'task.deleted') reload();
  });

  const tasks = data ?? [];
  const assigneeById = new Map((assignees ?? []).map((a) => [a.id, a]));
  const toast = useToast();
  const canWrite = can('task:write');
  const selection = useSelection(tasks.map((t) => t.id));

  const bulkComplete = async () => {
    const result = await runBulk(selection.selectedIds, (id) => api.patch(`/api/tasks/${id}`, { state: 'done' }));
    const { message, intent } = bulkResultMessage('Completed', result);
    toast(message, intent);
    selection.clear();
    reload();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Tasks</h1>
          <div className="page-subtitle">
            {tasks.length} {state === 'open' ? 'open' : ''} task{tasks.length === 1 ? '' : 's'}
          </div>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={15} /> New task
          </button>
        )}
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 4, flexWrap: 'wrap' }}>
        {(
          [
            ['open', 'Open'],
            ['in_progress', 'In progress'],
            ['done', 'Done'],
            ['', 'All'],
          ] as const
        ).map(([value, label]) => (
          <button key={label} className={`btn btn-sm${state === value ? ' btn-primary' : ''}`} onClick={() => setState(value)}>
            {label}
          </button>
        ))}
      </div>

      {canWrite && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm" onClick={bulkComplete}>
            <CheckCheck size={12} /> Mark done
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={7} cols={5} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={<ListTodo size={20} />}
            title={state === 'open' ? 'Nothing open' : 'No tasks'}
            text={state === 'open' ? 'Nothing is waiting on anyone.' : 'Nothing matched these filters.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th>Title</th>
                  <th style={{ width: 140 }}>Assigned to</th>
                  <th style={{ width: 92 }}>Priority</th>
                  <th style={{ width: 100 }}>State</th>
                  <th data-align="right" style={{ width: 130 }}>
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id} data-clickable="true" onClick={() => navigate(`/tasks/${task.id}`)}>
                    {canWrite && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <RowCheckbox selection={selection} id={task.id} />
                      </td>
                    )}
                    <td>
                      <div style={{ fontWeight: 550 }}>{task.title}</div>
                      {task.dueAt && (
                        <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                          due {formatRelative(task.dueAt)}
                        </div>
                      )}
                    </td>
                    <td>{task.userId ? assigneeById.get(task.userId)?.name ?? '—' : '—'}</td>
                    <td>
                      <Badge intent={PRIORITY_INTENT[task.priority]}>{task.priority}</Badge>
                    </td>
                    <td>
                      <Badge intent={autoIntent(task.state)}>{task.state.replace('_', ' ')}</Badge>
                    </td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(task.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="New task">
        <TaskForm
          assignees={assignees ?? []}
          onCreated={(id) => {
            setCreateOpen(false);
            reload();
            navigate(`/tasks/${id}`);
          }}
        />
      </Drawer>
    </>
  );
}

interface TaskDetailData extends Task {
  customer: Customer | null;
  resource: Resource | null;
}

export function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data, loading, error, reload } = useFetch(() => api.get<TaskDetailData>(`/api/tasks/${id}`), [id]);
  const { data: assignees } = useFetch(
    () => (can('task:write') ? api.get<TaskAssignee[]>('/api/tasks/assignees') : Promise.resolve([])),
    [],
  );

  const setField = async (patch: Record<string, unknown>) => {
    try {
      await api.patch(`/api/tasks/${id}`, patch);
      reload();
    } catch (err) {
      toast('Update failed', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  if (loading && !data) return <SkeletonTable rows={5} cols={2} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Task not found" />;

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div className="grow">
          <div className="row" style={{ gap: 8 }}>
            <Badge intent={PRIORITY_INTENT[data.priority]}>{data.priority}</Badge>
            <Badge intent={autoIntent(data.state)}>{data.state.replace('_', ' ')}</Badge>
          </div>
          <h1 className="page-title" style={{ marginTop: 8 }}>
            {data.title}
          </h1>
          <div className="page-subtitle">
            Created {formatRelative(data.createdAt)}
            {data.dueAt && ` · due ${formatRelative(data.dueAt)}`}
          </div>
        </div>

        {can('task:write') && (
          <div className="row">
            <select className="select" style={{ width: 130 }} value={data.state} onChange={(e) => void setField({ state: e.target.value })}>
              {['open', 'in_progress', 'done', 'cancelled'].map((s) => (
                <option key={s} value={s}>
                  {s.replace('_', ' ')}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ width: 110 }}
              value={data.priority}
              onChange={(e) => void setField({ priority: e.target.value })}
            >
              {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button className="btn btn-sm" onClick={() => setEditOpen(true)}>
              Edit
            </button>
          </div>
        )}
      </div>

      {data.description && (
        <div
          className="card"
          style={{
            marginBottom: 14,
            background: 'color-mix(in srgb, var(--itr-warning) 14%, var(--itr-surface))',
            borderColor: 'color-mix(in srgb, var(--itr-warning) 32%, transparent)',
          }}
        >
          <div className="card-body" style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.6 }}>
            {data.description}
          </div>
        </div>
      )}

      {(data.customer || data.resource) && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Linked to</span>
          </div>
          <div className="card-body col" style={{ gap: 10 }}>
            {data.customer && (
              <div className="row" style={{ gap: 8, cursor: 'pointer' }} onClick={() => navigate(`/clients/${data.customer!.id}`)}>
                <span className="subtle" style={{ fontSize: 12.5, width: 80 }}>
                  Client
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 550, color: 'var(--itr-primary)' }}>{data.customer.name}</span>
              </div>
            )}
            {data.resource && (
              <div className="row" style={{ gap: 8, cursor: 'pointer' }} onClick={() => navigate(`/resources/${data.resource!.id}`)}>
                <span className="subtle" style={{ fontSize: 12.5, width: 80 }}>
                  Resource
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 550, color: 'var(--itr-primary)' }}>
                  <ReachDot health={data.resource.health} />
                  {data.resource.name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title="Edit task">
        <TaskEditForm
          task={data}
          assignees={assignees ?? []}
          onSaved={() => {
            setEditOpen(false);
            reload();
          }}
        />
      </Drawer>
    </div>
  );
}

/* ================================================================== */

/**
 * Resources the task may be linked to, narrowed to the client picked on the
 * form — an estate of a few hundred devices is unusable as one flat list, and
 * a task linked to another client's device is always a mistake. No client
 * selected means the full list (internal kit included).
 */
function useClientResources(customerId: string) {
  const { data, loading } = useFetch(
    () => api.get<Resource[]>('/api/resources', { limit: 500, customerId: customerId || undefined }),
    [customerId],
  );
  return { resources: data ?? [], loading };
}

function ResourceField({
  customerId,
  value,
  onChange,
}: {
  customerId: string;
  value: string;
  onChange: (resourceId: string) => void;
}) {
  const { resources, loading } = useClientResources(customerId);

  return (
    <div className="field">
      <label className="field-label">Resource (optional)</label>
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {/* A task already linked to a resource outside the client's estate keeps
            its link visible rather than silently reading as "None". */}
        {value && !loading && !resources.some((r) => r.id === value) && <option value={value}>Linked resource (other client)</option>}
        {resources.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      {!loading && customerId && resources.length === 0 && (
        <div className="subtle" style={{ fontSize: 11.5, marginTop: 4 }}>
          This client has no resources yet.
        </div>
      )}
    </div>
  );
}

export function TaskForm({
  assignees,
  defaultCustomerId,
  defaultResourceId,
  onCreated,
}: {
  assignees: TaskAssignee[];
  defaultCustomerId?: string | null;
  defaultResourceId?: string | null;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const { data: customers } = useFetch(() => api.get<Customer[]>('/api/customers'), []);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    userId: '',
    customerId: defaultCustomerId ?? '',
    resourceId: defaultResourceId ?? '',
    dueAt: '',
  });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const task = await api.post<Task>('/api/tasks', {
        title: form.title.trim(),
        description: form.description,
        priority: form.priority,
        userId: form.userId || null,
        customerId: form.customerId || null,
        resourceId: form.resourceId || null,
        dueAt: form.dueAt ? new Date(form.dueAt).getTime() : null,
      });
      onCreated(task.id);
    } catch (err) {
      toast('Could not create the task', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Title</label>
        <input className="input" required autoFocus value={form.title} onChange={set('title')} placeholder="What needs to be done?" />
      </div>

      <div className="field">
        <label className="field-label">Description</label>
        <textarea className="textarea" value={form.description} onChange={set('description')} placeholder="Details" />
      </div>

      <div className="field">
        <label className="field-label">Assign to</label>
        <select className="select" value={form.userId} onChange={set('userId')}>
          <option value="">Unassigned</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Priority</label>
        <select className="select" value={form.priority} onChange={set('priority')}>
          {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Due date</label>
        <input className="input" type="date" value={form.dueAt} onChange={set('dueAt')} />
      </div>

      <div className="field">
        <label className="field-label">Client (optional)</label>
        <select
          className="select"
          value={form.customerId}
          onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value, resourceId: '' }))}
        >
          <option value="">None</option>
          {(customers ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <ResourceField
        customerId={form.customerId}
        value={form.resourceId}
        onChange={(resourceId) => setForm((f) => ({ ...f, resourceId }))}
      />

      <button className="btn btn-primary" type="submit" disabled={busy || !form.title.trim()} style={{ width: '100%' }}>
        {busy ? 'Creating…' : 'Create task'}
      </button>
    </form>
  );
}

/**
 * Edit an existing task in place — used by the dashboard's "My tasks" panel
 * so a task can be reassigned, rescheduled or re-linked without navigating
 * away to the full Tasks page.
 */
export function TaskEditForm({ task, assignees, onSaved }: { task: Task; assignees: TaskAssignee[]; onSaved: () => void }) {
  const toast = useToast();
  const { data: customers } = useFetch(() => api.get<Customer[]>('/api/customers'), []);
  const [form, setForm] = useState({
    title: task.title,
    description: task.description,
    state: task.state,
    priority: task.priority,
    userId: task.userId ?? '',
    customerId: task.customerId ?? '',
    resourceId: task.resourceId ?? '',
    dueAt: task.dueAt ? new Date(task.dueAt).toISOString().slice(0, 10) : '',
  });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/api/tasks/${task.id}`, {
        title: form.title.trim(),
        description: form.description,
        state: form.state,
        priority: form.priority,
        userId: form.userId || null,
        customerId: form.customerId || null,
        resourceId: form.resourceId || null,
        dueAt: form.dueAt ? new Date(form.dueAt).getTime() : null,
      });
      onSaved();
    } catch (err) {
      toast('Could not save the task', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Title</label>
        <input className="input" required autoFocus value={form.title} onChange={set('title')} />
      </div>

      <div className="field">
        <label className="field-label">Description</label>
        <textarea className="textarea" value={form.description} onChange={set('description')} />
      </div>

      <div className="row" style={{ gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">State</label>
          <select className="select" value={form.state} onChange={set('state')}>
            {['open', 'in_progress', 'done', 'cancelled'].map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label">Priority</label>
          <select className="select" value={form.priority} onChange={set('priority')}>
            {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Assign to</label>
        <select className="select" value={form.userId} onChange={set('userId')}>
          <option value="">Unassigned</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Due date</label>
        <input className="input" type="date" value={form.dueAt} onChange={set('dueAt')} />
      </div>

      <div className="field">
        <label className="field-label">Client (optional)</label>
        <select
          className="select"
          value={form.customerId}
          onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value, resourceId: '' }))}
        >
          <option value="">None</option>
          {(customers ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <ResourceField
        customerId={form.customerId}
        value={form.resourceId}
        onChange={(resourceId) => setForm((f) => ({ ...f, resourceId }))}
      />

      <button className="btn btn-primary" type="submit" disabled={busy || !form.title.trim()} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}
